// src/context/AuthContext.jsx - Profound Philosophy Auth with Storage Controls
// localStorage keys use the "profoundPhilosophy_" prefix
//
// Sign-in is hub-first: redirectToAuth sends the user to the DDD auth hub,
// and the token-exchange callback signs the hub custom token into the hub
// Firebase app (services/firebase.js). The listener below observes BOTH the
// own-project session and the hub session — the own-project session wins
// when both exist, and the hub session alone counts as signed in (the hub
// does not mint a spoke token for this app, so real SSO flows produce a hub
// session only).

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, authHubAuth, db } from '../services/firebase';
import { authHubService } from '../services/authHubService';
import { clearCloudData as clearCloudDataService } from '../services/storageService';
import { resetHydrateCache, flushPatch } from '../services/hubAppState';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

const toUserData = (firebaseUser, viaHub) => ({
  uid: firebaseUser.uid,
  email: firebaseUser.email,
  displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
  authType: viaHub
    ? 'hub'
    : firebaseUser.isAnonymous
      ? 'anonymous'
      : (firebaseUser.providerData[0]?.providerId.includes('google') ? 'google' : 'email'),
  isAnonymous: firebaseUser.isAnonymous
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storagePreference, setStoragePreference] = useState('both'); // 'local', 'cloud', 'both'

  useEffect(() => {
    // Check for existing auth session
    const checkAuthStatus = () => {
      try {
        // Load storage preference
        const savedPreference = localStorage.getItem('profoundPhilosophy_storagePreference');
        if (savedPreference) {
          setStoragePreference(savedPreference);
        }

        // Check for auth data in localStorage (pre-paint state only — the
        // Firebase listeners below are authoritative and overwrite this)
        const isSignedIn = localStorage.getItem('profoundPhilosophy_isSignedIn');
        const userId = localStorage.getItem('profoundPhilosophy_userId');
        const userEmail = localStorage.getItem('profoundPhilosophy_userEmail');
        const displayName = localStorage.getItem('profoundPhilosophy_displayName');
        const authType = localStorage.getItem('profoundPhilosophy_authType');

        if (isSignedIn === 'true' && userId) {
          setUser({
            uid: userId,
            email: userEmail,
            displayName: displayName,
            authType: authType,
            isAnonymous: authType === 'anonymous'
          });
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setUser(null);
      }
    };

    checkAuthStatus();

    // Observe both sessions; emit the own-project user when present,
    // otherwise the hub user, otherwise null.
    let ownUser = auth?.currentUser || null;
    let hubUser = authHubAuth?.currentUser || null;

    const emit = () => {
      const firebaseUser = ownUser || hubUser;
      if (firebaseUser) {
        const userData = toUserData(firebaseUser, firebaseUser === hubUser && !ownUser);
        setUser(userData);

        // Sync to localStorage if preference allows
        const currentPreference = localStorage.getItem('profoundPhilosophy_storagePreference') || 'both';
        if (currentPreference === 'local' || currentPreference === 'both') {
          localStorage.setItem('profoundPhilosophy_isSignedIn', 'true');
          localStorage.setItem('profoundPhilosophy_userId', userData.uid);
          localStorage.setItem('profoundPhilosophy_userEmail', userData.email || '');
          localStorage.setItem('profoundPhilosophy_displayName', userData.displayName);
          localStorage.setItem('profoundPhilosophy_authType', userData.authType);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    const unsubscribes = [];
    if (auth) {
      unsubscribes.push(onAuthStateChanged(auth, (u) => { ownUser = u; emit(); }));
    }
    if (authHubAuth) {
      unsubscribes.push(onAuthStateChanged(authHubAuth, (u) => { hubUser = u; emit(); }));
    }
    if (!auth && !authHubAuth) setLoading(false);

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, []);

  const redirectToAuth = (action = 'signin') => {
    const returnPath = window.location.pathname + window.location.search;
    if (action === 'signup') {
      authHubService.redirectToSignup(returnPath);
    } else {
      authHubService.redirectToLogin(returnPath);
    }
  };

  const logOut = async () => {
    try {
      // Push any queued hub patch before the session that authenticates it
      // goes away, then sign out of both sessions.
      await flushPatch().catch(() => {});
      await authHubService.signOut();
      resetHydrateCache();

      // Clear localStorage
      localStorage.removeItem('profoundPhilosophy_isSignedIn');
      localStorage.removeItem('profoundPhilosophy_userId');
      localStorage.removeItem('profoundPhilosophy_userEmail');
      localStorage.removeItem('profoundPhilosophy_displayName');
      localStorage.removeItem('profoundPhilosophy_authType');

      // Clear user state
      setUser(null);

      console.log('Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const updateStoragePreference = (preference) => {
    setStoragePreference(preference);
    localStorage.setItem('profoundPhilosophy_storagePreference', preference);

    // If switching to cloud-only, clear local data (except preference)
    if (preference === 'cloud') {
      // Keep auth data but user can manually clear module progress etc.
      console.log('Switched to cloud-only storage');
    }

    // If switching to local-only, warn about no sync
    if (preference === 'local') {
      console.log('Switched to local-only storage - data will not sync');
    }
  };

  const clearLocalData = () => {
    // Clear all Profound Philosophy data from localStorage
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('profoundPhilosophy_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('Local data cleared');
  };

  const clearCloudData = async () => {
    if (!user) return;

    try {
      // Clear hub thin-layer data for this user using storage service
      console.log('Cloud data clear initiated for user:', user.uid);
      await clearCloudDataService(user.uid);
      console.log('✅ Cloud data cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing cloud data:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAnonymous: user?.isAnonymous || false,
    displayName: user?.displayName || 'Guest',

    // Storage preferences
    storagePreference,
    updateStoragePreference,
    clearLocalData,
    clearCloudData,
    usesLocal: storagePreference === 'local' || storagePreference === 'both',
    usesCloud: storagePreference === 'cloud' || storagePreference === 'both',

    // Auth actions
    redirectToAuth,
    logOut,

    // Firebase instances (for services that need them)
    auth,
    db
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
